import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { CustomerActions } from '../actions/customer.actions';
import { Customer } from '../models/customer.model';
import { CustomerService } from 'src/app/services/customer.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged, sameRows } from './state-content-changed'

export class CustomerStateModel {
    is_refreshing: boolean
    customer: Customer[]
    version?: string
}

@State<CustomerStateModel>({
    name: 'customer',
    defaults: {
        is_refreshing: true,
        customer: []
    }
})
@Injectable()
export class CustomerState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private customerService: CustomerService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<CustomerStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                       
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    }   

    async ngxsOnChanges(change: NgxsSimpleChange) {                     
        if (this.storageService.is_initialized && stateContentChanged(change)) {
            const state_model: CustomerStateModel = change.currentValue
            this.storageService.set('customers', JSON.stringify(state_model))   
        }  
    }

    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('customers');        
        if (json) {
            const state_model: CustomerStateModel = JSON.parse(json)            
            if (state_model) {                
                this.store.dispatch(new CustomerActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new CustomerActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(CustomerState.getState)
        const ret = await this.customerService.getAll(held.customer?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new CustomerActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: CustomerStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: CustomerStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: CustomerStateModel) {
        return state.customer
    }

    @Action(CustomerActions.Refresh)
    async refresh({patchState, getState}: StateContext<CustomerStateModel>, {  }:CustomerActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false, customer: sameRows(getState().customer)})
        }
    }

    @Action(CustomerActions.Init)
    init({setState, getState}: StateContext<CustomerStateModel>, { payload }:CustomerActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new CustomerActions.Refresh())
    }
    
    @Action(CustomerActions.Set)
    set({setState}: StateContext<CustomerStateModel>, { payload, version }:CustomerActions.Set) {             
        setState({   
            is_refreshing: false,         
            customer: payload,
            version
        })
    }
}
